import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Search, Pencil, Trash2, AlertTriangle, User } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

export default function EmployeeReportsPage({ embedded }) {
  const { isRole } = useAuth();
  const [reports, setReports] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ employee_id: '', report_date: '', category: 'generico', severity: 'media', description: '', action_taken: '', notes: '' });

  const load = async () => {
    try {
      const params = {};
      if (filterEmployee) params.employee_id = filterEmployee;
      if (search) params.search = search;
      const [rRes, eRes] = await Promise.all([api.get('/employee-reports', { params }), api.get('/employees')]);
      setReports(rRes.data);
      setEmployees(eRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filterEmployee, search]);

  const handleSave = async () => {
    try {
      if (editing) { await api.put(`/employee-reports/${editing.id}`, form); }
      else { await api.post('/employee-reports', form); }
      setShowDialog(false); setEditing(null);
      setForm({ employee_id: '', report_date: new Date().toISOString().split('T')[0], category: 'generico', severity: 'media', description: '', action_taken: '', notes: '' });
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleEdit = (r) => {
    setEditing(r);
    setForm({ employee_id: r.employee_id, report_date: r.report_date, category: r.category, severity: r.severity, description: r.description, action_taken: r.action_taken || '', notes: r.notes || '' });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa segnalazione?')) return;
    try { await api.delete(`/employee-reports/${id}`); load(); } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const severityBadge = (sev) => {
    const colors = { bassa: 'bg-blue-100 text-blue-800', media: 'bg-amber-100 text-amber-800', alta: 'bg-orange-100 text-orange-800', critica: 'bg-red-100 text-red-800' };
    return <Badge className={colors[sev] || 'bg-slate-100'}>{sev}</Badge>;
  };

  const categoryLabel = (cat) => {
    const labels = { generico: 'Generico', comportamento: 'Comportamento', sicurezza: 'Sicurezza', qualita: 'Qualita', ritardo: 'Ritardo', assenza: 'Assenza ingiustificata' };
    return labels[cat] || cat;
  };

  // Count reports per employee
  const reportCounts = {};
  reports.forEach(r => { reportCounts[r.employee_id] = (reportCounts[r.employee_id] || 0) + 1; });

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;

  return (
    <div className="space-y-4" data-testid="employee-reports-page">
      {!embedded && <h1 className="text-2xl font-bold text-slate-900">Segnalazioni Dipendenti</h1>}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-blue-50 border-blue-200"><CardContent className="p-4"><p className="text-xs text-blue-600">Totale</p><p className="text-xl font-bold text-blue-800">{reports.length}</p></CardContent></Card>
        <Card className="bg-red-50 border-red-200"><CardContent className="p-4"><p className="text-xs text-red-600">Critiche</p><p className="text-xl font-bold text-red-800">{reports.filter(r => r.severity === 'critica').length}</p></CardContent></Card>
        <Card className="bg-orange-50 border-orange-200"><CardContent className="p-4"><p className="text-xs text-orange-600">Alte</p><p className="text-xl font-bold text-orange-800">{reports.filter(r => r.severity === 'alta').length}</p></CardContent></Card>
        <Card className="bg-amber-50 border-amber-200"><CardContent className="p-4"><p className="text-xs text-amber-600">Medie</p><p className="text-xl font-bold text-amber-800">{reports.filter(r => r.severity === 'media').length}</p></CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <div className="flex gap-2 flex-1">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                <Input placeholder="Cerca..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
              </div>
              <select className="h-9 rounded-md border border-slate-200 px-3 text-sm" value={filterEmployee} onChange={(e) => setFilterEmployee(e.target.value)}>
                <option value="">Tutti i dipendenti</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>)}
              </select>
            </div>
            <Button onClick={() => { setEditing(null); setForm({ employee_id: '', report_date: new Date().toISOString().split('T')[0], category: 'generico', severity: 'media', description: '', action_taken: '', notes: '' }); setShowDialog(true); }}>
              <Plus size={16} className="mr-1" /> Nuova Segnalazione
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Data</TableHead><TableHead>Dipendente</TableHead><TableHead>Categoria</TableHead>
              <TableHead>Gravita</TableHead><TableHead>Descrizione</TableHead><TableHead>Provvedimento</TableHead><TableHead className="w-[80px]">Azioni</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {reports.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Nessuna segnalazione</TableCell></TableRow>
              ) : reports.map(r => (
                <TableRow key={r.id} className="hover:bg-slate-50">
                  <TableCell className="text-sm">{formatDate(r.report_date)}</TableCell>
                  <TableCell className="font-semibold">{r.employee_name}</TableCell>
                  <TableCell><Badge variant="outline">{categoryLabel(r.category)}</Badge></TableCell>
                  <TableCell>{severityBadge(r.severity)}</TableCell>
                  <TableCell className="text-sm max-w-[250px] truncate">{r.description}</TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate text-slate-500">{r.action_taken || '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(r)} className="p-1 h-auto"><Pencil size={14} className="text-blue-600" /></Button>
                      {isRole('admin', 'aspp') && <Button variant="ghost" size="sm" onClick={() => handleDelete(r.id)} className="p-1 h-auto"><Trash2 size={14} className="text-red-600" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Modifica Segnalazione' : 'Nuova Segnalazione'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Dipendente *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({...form, employee_id: v})}>
                <SelectTrigger><SelectValue placeholder="Seleziona dipendente..." /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Data</Label><Input type="date" value={form.report_date} onChange={(e) => setForm({...form, report_date: e.target.value})} /></div>
              <div><Label>Categoria</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="generico">Generico</SelectItem>
                    <SelectItem value="comportamento">Comportamento</SelectItem>
                    <SelectItem value="sicurezza">Sicurezza</SelectItem>
                    <SelectItem value="qualita">Qualita</SelectItem>
                    <SelectItem value="ritardo">Ritardo</SelectItem>
                    <SelectItem value="assenza">Assenza ingiustificata</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Gravita</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({...form, severity: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bassa">Bassa</SelectItem>
                    <SelectItem value="media">Media</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="critica">Critica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Descrizione *</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} placeholder="Descrivi la segnalazione..." /></div>
            <div><Label>Provvedimento preso</Label><Textarea value={form.action_taken} onChange={(e) => setForm({...form, action_taken: e.target.value})} rows={2} /></div>
            <div><Label>Note</Label><Input value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={!form.employee_id || !form.description.trim()}>{editing ? 'Aggiorna' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
